import { type Level } from '../models/level';
import { isWorldOpen, OPEN_WORLDS, problemTypeLabel, worldById } from '../models/world';
import { generateRandomLevel } from './levelGenerator';

// Daily challenges are keyed by UTC calendar day, not the viewer's local day.
// The web build has no per-device save file to reconcile against a server, so
// "same day worldwide" has to mean the same instant-independent calendar day
// for every viewer regardless of timezone — UTC is the only zone that means
// the same thing to everyone. The challenge therefore flips at UTC midnight,
// not at each player's local midnight. This is a deliberate deviation from
// the Flutter client (which reads DateTime.now() in local time on a
// single-device, offline-only install, where "today" needs no cross-viewer
// agreement).

const DAILY_DIFFICULTY = 5;

const WEEKDAY_TO_WORLD_INDEX: Readonly<Record<number, number>> = {
  0: 2, // Sunday — Shortest Path (world 2)
  1: 1, // Monday — Shortest Path (world 1)
  2: 3, // Tuesday — MST
  3: 4, // Wednesday — Max Flow
  4: 5, // Thursday — Min Cut
  5: 6, // Friday — Matching
  6: 7, // Saturday — Interdiction
};

export interface DailyChallenge {
  /** UTC midnight of the challenge's calendar day. */
  readonly date: Date;
  readonly levelId: string;
  readonly worldIndex: number;
  readonly difficulty: number;
  readonly seed: number;
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

function utcDateParts(date: Date): { year: number; month: number; day: number } {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

function openWorldFor(worldIndex: number, weekday: number): number {
  if (isWorldOpen(worldIndex)) return worldIndex;
  return OPEN_WORLDS[weekday % OPEN_WORLDS.length]?.id ?? worldIndex;
}

/** The deterministic challenge for the UTC calendar day of `date`. */
export function dailyChallengeForDate(date: Date): DailyChallenge {
  const { year, month, day } = utcDateParts(date);
  const weekday = date.getUTCDay();

  return {
    date: new Date(Date.UTC(year, month - 1, day)),
    levelId: `daily_${pad(year, 4)}${pad(month, 2)}${pad(day, 2)}`,
    worldIndex: openWorldFor(WEEKDAY_TO_WORLD_INDEX[weekday]!, weekday),
    difficulty: DAILY_DIFFICULTY,
    seed: year * 10000 + month * 100 + day,
  };
}

export function dailyChallengeForToday(): DailyChallenge {
  return dailyChallengeForDate(new Date());
}

/**
 * Generates the actual playable Level for a DailyChallenge. The level carries
 * the challenge's own id so runs are recorded per calendar day, not per seed.
 */
export function generateDailyChallengeLevel(challenge: DailyChallenge): Level {
  const level = generateRandomLevel({
    worldIndex: challenge.worldIndex,
    difficulty: challenge.difficulty,
    seed: challenge.seed,
  });
  return {
    ...level,
    id: challenge.levelId,
    title: `Dispatch ${dateToIsoString(challenge.date)}`,
  };
}

export function problemTypeNameForDate(date: Date): string {
  const world = worldById(dailyChallengeForDate(date).worldIndex);
  return world ? problemTypeLabel(world.problemType) : 'Unknown';
}

export function isDailyChallengeLevel(levelId: string): boolean {
  return levelId.startsWith('daily_');
}

/** Parses a `daily_YYYYMMDD` level id back into its UTC-midnight Date, or null. */
export function parseDailyLevelId(levelId: string): Date | null {
  if (!isDailyChallengeLevel(levelId)) return null;

  const datePart = levelId.slice(6);
  if (!/^\d{8}$/.test(datePart)) return null;

  const year = Number(datePart.slice(0, 4));
  const month = Number(datePart.slice(4, 6));
  const day = Number(datePart.slice(6, 8));
  return new Date(Date.UTC(year, month - 1, day));
}

export function dateToIsoString(date: Date): string {
  const { year, month, day } = utcDateParts(date);
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

// ─── Streak tracking ────────────────────────────────────────────────────────

export type StreakMilestone = 'none' | 'bronze' | 'silver' | 'gold';

export interface DailyStreak {
  readonly currentStreak: number;
  readonly longestStreak: number;
  readonly lastCompletedDate: Date | null;
  /** ISO date strings (yyyy-MM-dd) for every completed daily challenge. */
  readonly completedDates: ReadonlySet<string>;
}

export const EMPTY_DAILY_STREAK: DailyStreak = {
  currentStreak: 0,
  longestStreak: 0,
  lastCompletedDate: null,
  completedDates: new Set(),
};

export function streakMilestone(streak: DailyStreak): StreakMilestone {
  if (streak.currentStreak >= 100) return 'gold';
  if (streak.currentStreak >= 30) return 'silver';
  if (streak.currentStreak >= 7) return 'bronze';
  return 'none';
}

export function withDailyStreak(streak: DailyStreak, overrides: Partial<DailyStreak>): DailyStreak {
  return { ...streak, ...overrides };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDayNumber(date: Date): number {
  return Math.floor(date.getTime() / DAY_MS);
}

/**
 * Derives the streak from the `daily_*` entries of a progress map, as of the
 * UTC day of `today`. A streak survives until the end of today: missing today
 * keeps yesterday's run alive, missing yesterday resets it.
 */
export function dailyStreakFromProgress(
  progress: Readonly<Record<string, { readonly stars: number }>>,
  today: Date = new Date(),
): DailyStreak {
  const completed = new Map<number, Date>();
  for (const [id, entry] of Object.entries(progress)) {
    const date = entry.stars > 0 ? parseDailyLevelId(id) : null;
    if (date && !Number.isNaN(date.getTime())) completed.set(utcDayNumber(date), date);
  }
  if (completed.size === 0) return EMPTY_DAILY_STREAK;

  const days = [...completed.keys()].sort((a, b) => a - b);
  let longestStreak = 1;
  let run = 1;
  for (let i = 1; i < days.length; i++) {
    run = days[i]! - days[i - 1]! === 1 ? run + 1 : 1;
    longestStreak = Math.max(longestStreak, run);
  }

  const todayNumber = utcDayNumber(today);
  let cursor = completed.has(todayNumber) ? todayNumber : todayNumber - 1;
  let currentStreak = 0;
  while (completed.has(cursor)) {
    currentStreak++;
    cursor--;
  }

  return {
    currentStreak,
    longestStreak,
    lastCompletedDate: completed.get(days[days.length - 1]!) ?? null,
    completedDates: new Set(days.map((d) => dateToIsoString(completed.get(d)!))),
  };
}
